type VideoElementAttachmentPhase = "unbound" | "active" | "awaiting-ref-cleanup";

/** One ref callback identity returned by `registerVideo(channelId)`. */
export interface VideoElementAttachment {
  readonly token: symbol;
  readonly channelId: string;
}

interface ActiveAttachmentState {
  phase: "active";
  element: HTMLVideoElement;
  unsubscribe: (() => void) | null;
}

interface AwaitingRefCleanupState {
  phase: "awaiting-ref-cleanup";
  element: null;
  unsubscribe: null;
}

type OutstandingAttachmentState = ActiveAttachmentState | AwaitingRefCleanupState;

interface OutstandingAttachment {
  attachment: VideoElementAttachment;
  state: OutstandingAttachmentState;
}

interface PendingVideoRelease {
  token: symbol;
  channelId: string;
  element: HTMLVideoElement;
}

interface VideoElementAttachmentControllerOptions {
  releaseResource: (element: HTMLVideoElement) => void;
  enqueueMicrotask?: (callback: VoidFunction) => void;
}

interface AttachVideoElementOptions {
  channelIsActive: boolean;
  subscribe: (isCurrent: () => boolean) => (() => void) | null;
}

export interface VideoElementAttachmentState {
  phase: VideoElementAttachmentPhase;
  channelId: string;
  element: HTMLVideoElement | null;
  isActiveForChannel: boolean;
  isCurrentResourceOwner: boolean;
}

export interface VideoElementAttachmentCounts {
  outstandingAttachments: number;
  activeChannels: number;
  activeElements: number;
  queuedAttachments: number;
  pendingReleases: number;
}

const defaultEnqueueMicrotask = (callback: VoidFunction) => {
  if (typeof queueMicrotask === "function") {
    queueMicrotask(callback);
  } else {
    void Promise.resolve().then(callback);
  }
};

/**
 * Resource ownership must span hook instances because a stable DOM video can move to a new
 * timeline without changing element identity. Pending release tokens make a previous controller's
 * deferred cleanup harmless as soon as another attachment claims that element.
 */
class VideoElementResourceOwnershipRegistry {
  private readonly owners = new WeakMap<
    HTMLVideoElement,
    { token: symbol; onDisplaced: VoidFunction }
  >();
  private readonly pendingReleaseTokens = new WeakMap<HTMLVideoElement, symbol>();

  isClaimed(element: HTMLVideoElement): boolean {
    return this.owners.has(element);
  }

  isOwnedBy(element: HTMLVideoElement, attachmentToken: symbol): boolean {
    return this.owners.get(element)?.token === attachmentToken;
  }

  claim(element: HTMLVideoElement, attachmentToken: symbol, onDisplaced: VoidFunction): void {
    this.pendingReleaseTokens.delete(element);
    const previousOwner = this.owners.get(element);
    this.owners.set(element, { token: attachmentToken, onDisplaced });
    if (previousOwner && previousOwner.token !== attachmentToken) {
      previousOwner.onDisplaced();
    }
  }

  relinquish(element: HTMLVideoElement, attachmentToken: symbol): void {
    if (this.owners.get(element)?.token === attachmentToken) {
      this.owners.delete(element);
    }
  }

  markPendingRelease(element: HTMLVideoElement, releaseToken: symbol): void {
    this.pendingReleaseTokens.set(element, releaseToken);
  }

  cancelPendingRelease(element: HTMLVideoElement, releaseToken?: symbol): void {
    if (releaseToken === undefined || this.pendingReleaseTokens.get(element) === releaseToken) {
      this.pendingReleaseTokens.delete(element);
    }
  }

  consumePendingRelease(element: HTMLVideoElement, releaseToken: symbol): boolean {
    if (this.pendingReleaseTokens.get(element) !== releaseToken) return false;
    this.pendingReleaseTokens.delete(element);
    return !this.owners.has(element);
  }
}

const sharedVideoElementOwnership = new VideoElementResourceOwnershipRegistry();

/**
 * Internal imperative state machine for `useSyncedVideoTimeline` video refs.
 *
 * - channel ID: stable logical playback slot
 * - attachment: one returned ref callback/consumer lifetime
 * - element: physical media resource/decoder identity
 *
 * The outstanding map is the source of truth. Channel, element, and FIFO maps are private indexes
 * updated only by this controller's transitions; the hook never combines those indexes itself.
 */
export class VideoElementAttachmentController {
  private readonly outstandingByToken = new Map<symbol, OutstandingAttachment>();
  private readonly activeByChannel = new Map<string, OutstandingAttachment>();
  private readonly activeByElement = new Map<HTMLVideoElement, OutstandingAttachment>();
  private readonly attachmentQueues = new Map<string, OutstandingAttachment[]>();
  private readonly pendingReleases = new Map<HTMLVideoElement, PendingVideoRelease>();
  private readonly previouslyAttached = new WeakSet<VideoElementAttachment>();
  private readonly releaseResource: (element: HTMLVideoElement) => void;
  private readonly enqueueMicrotask: (callback: VoidFunction) => void;

  constructor({
    releaseResource,
    enqueueMicrotask = defaultEnqueueMicrotask,
  }: VideoElementAttachmentControllerOptions) {
    this.releaseResource = releaseResource;
    this.enqueueMicrotask = enqueueMicrotask;
  }

  createAttachment(channelId: string): VideoElementAttachment {
    return {
      token: Symbol(`c6-video-attachment:${channelId}`),
      channelId,
    };
  }

  attach(
    attachment: VideoElementAttachment,
    element: HTMLVideoElement,
    { channelIsActive, subscribe }: AttachVideoElementOptions,
  ): boolean {
    const current = this.outstandingByToken.get(attachment.token);
    if (channelIsActive && current?.state.phase === "active" && current.state.element === element) {
      return false;
    }

    // A later null for any callback that has received an element is identity-scoped, even when a
    // concurrent channels render has already made that setup ineligible.
    this.previouslyAttached.add(attachment);
    if (current) {
      this.detachOutstanding(current, { release: true, settle: true });
    }

    if (!channelIsActive) {
      this.releaseUnclaimedNow(element);
      return false;
    }

    this.cancelOwnedPendingRelease(element);

    // A pending release may belong to a previous hook instance. Claiming the element invalidates
    // that controller's shared token even though its private microtask is still queued.
    sharedVideoElementOwnership.cancelPendingRelease(element);

    const previousElementAttachment = this.activeByElement.get(element);
    if (previousElementAttachment && previousElementAttachment.attachment !== attachment) {
      this.detachOutstanding(previousElementAttachment, { release: false, settle: false });
    }

    const previousChannelAttachment = this.activeByChannel.get(attachment.channelId);
    if (previousChannelAttachment && previousChannelAttachment.attachment !== attachment) {
      const previousElement =
        previousChannelAttachment.state.phase === "active"
          ? previousChannelAttachment.state.element
          : null;
      this.detachOutstanding(previousChannelAttachment, {
        release: previousElement !== element,
        settle: false,
      });
    }

    const activeState: ActiveAttachmentState = {
      phase: "active",
      element,
      unsubscribe: null,
    };
    const outstanding: OutstandingAttachment = { attachment, state: activeState };
    this.outstandingByToken.set(attachment.token, outstanding);
    this.enqueueAttachment(outstanding);
    this.activeByChannel.set(attachment.channelId, outstanding);
    this.activeByElement.set(element, outstanding);
    sharedVideoElementOwnership.claim(element, attachment.token, () => {
      const claimedElsewhere = this.outstandingByToken.get(attachment.token);
      if (
        claimedElsewhere?.state.phase === "active" &&
        claimedElsewhere.state.element === element
      ) {
        this.detachOutstanding(claimedElsewhere, { release: false, settle: false });
      }
    });

    const unsubscribe = subscribe(() => this.isActive(attachment, element));
    if (this.isActive(attachment, element)) {
      activeState.unsubscribe = unsubscribe;
    } else {
      unsubscribe?.();
    }
    return true;
  }

  detachFromRef(attachment: VideoElementAttachment): void {
    const directAttachment = this.outstandingByToken.get(attachment.token);
    if (directAttachment) {
      this.detachOutstanding(directAttachment, { release: true, settle: true });
      return;
    }

    // Once a known attachment has settled, an extra stale null is a no-op. A never-attached
    // identity is the legacy `(node) => registerVideo(id)(node)` shape: its null callback cannot
    // carry the setup identity, so consume the oldest superseded attachment first (FIFO).
    if (this.previouslyAttached.has(attachment)) return;
    const queue = this.attachmentQueues.get(attachment.channelId);
    const legacyAttachment =
      queue?.find((candidate) => candidate.state.phase === "awaiting-ref-cleanup") ?? queue?.[0];
    if (legacyAttachment) {
      this.detachOutstanding(legacyAttachment, { release: true, settle: true });
    }
  }

  isActive(attachment: VideoElementAttachment, element: HTMLVideoElement): boolean {
    const outstanding = this.outstandingByToken.get(attachment.token);
    return (
      outstanding?.attachment === attachment &&
      outstanding.state.phase === "active" &&
      outstanding.state.element === element &&
      this.activeByChannel.get(attachment.channelId) === outstanding &&
      this.activeByElement.get(element) === outstanding &&
      sharedVideoElementOwnership.isOwnedBy(element, attachment.token)
    );
  }

  forEachActiveVideo(callback: (channelId: string, element: HTMLVideoElement) => void): void {
    for (const [channelId, outstanding] of this.activeByChannel) {
      if (outstanding.state.phase !== "active") continue;
      const { element } = outstanding.state;
      if (!sharedVideoElementOwnership.isOwnedBy(element, outstanding.attachment.token)) continue;
      callback(channelId, element);
    }
  }

  removeInactiveChannels(activeChannelIds: ReadonlySet<string>): void {
    const removedChannelIds = new Set<string>();
    for (const outstanding of [...this.outstandingByToken.values()]) {
      if (activeChannelIds.has(outstanding.attachment.channelId)) continue;
      removedChannelIds.add(outstanding.attachment.channelId);
      this.detachOutstanding(outstanding, { release: true, settle: true });
    }
    for (const pending of this.pendingReleases.values()) {
      if (!activeChannelIds.has(pending.channelId)) {
        removedChannelIds.add(pending.channelId);
      }
    }
    for (const channelId of removedChannelIds) {
      this.flushPendingReleases(channelId);
    }
  }

  dispose(): void {
    for (const outstanding of [...this.outstandingByToken.values()]) {
      this.detachOutstanding(outstanding, { release: true, settle: true });
    }
    this.flushPendingReleases();
    this.outstandingByToken.clear();
    this.activeByChannel.clear();
    this.activeByElement.clear();
    this.attachmentQueues.clear();
  }

  getAttachmentState(attachment: VideoElementAttachment): VideoElementAttachmentState {
    const outstanding = this.outstandingByToken.get(attachment.token);
    const state = outstanding?.state;
    const element = state?.phase === "active" ? state.element : null;
    return {
      phase: state?.phase ?? "unbound",
      channelId: attachment.channelId,
      element,
      isActiveForChannel:
        outstanding !== undefined && this.activeByChannel.get(attachment.channelId) === outstanding,
      isCurrentResourceOwner:
        element !== null && sharedVideoElementOwnership.isOwnedBy(element, attachment.token),
    };
  }

  getCounts(): VideoElementAttachmentCounts {
    let queuedAttachments = 0;
    for (const queue of this.attachmentQueues.values()) queuedAttachments += queue.length;
    return {
      outstandingAttachments: this.outstandingByToken.size,
      activeChannels: this.activeByChannel.size,
      activeElements: this.activeByElement.size,
      queuedAttachments,
      pendingReleases: this.pendingReleases.size,
    };
  }

  private detachOutstanding(
    outstanding: OutstandingAttachment,
    { release, settle }: { release: boolean; settle: boolean },
  ): void {
    const { attachment } = outstanding;
    const state = outstanding.state;
    if (state.phase === "active") {
      const { element } = state;
      state.unsubscribe?.();
      outstanding.state = {
        phase: "awaiting-ref-cleanup",
        element: null,
        unsubscribe: null,
      };

      if (this.activeByElement.get(element) === outstanding) {
        this.activeByElement.delete(element);
      }
      if (this.activeByChannel.get(attachment.channelId) === outstanding) {
        this.activeByChannel.delete(attachment.channelId);
      }
      sharedVideoElementOwnership.relinquish(element, attachment.token);

      if (release && !sharedVideoElementOwnership.isClaimed(element)) {
        this.scheduleRelease(attachment.channelId, element);
      }
    }

    if (settle) {
      if (this.outstandingByToken.get(attachment.token) === outstanding) {
        this.outstandingByToken.delete(attachment.token);
      }
      this.forgetAttachment(outstanding);
    }
  }

  private enqueueAttachment(outstanding: OutstandingAttachment): void {
    const { channelId } = outstanding.attachment;
    const queue = this.attachmentQueues.get(channelId);
    if (queue) {
      if (!queue.includes(outstanding)) queue.push(outstanding);
      return;
    }
    this.attachmentQueues.set(channelId, [outstanding]);
  }

  private forgetAttachment(outstanding: OutstandingAttachment): void {
    const { channelId } = outstanding.attachment;
    const queue = this.attachmentQueues.get(channelId);
    if (!queue) return;
    const index = queue.indexOf(outstanding);
    if (index !== -1) queue.splice(index, 1);
    if (queue.length === 0) this.attachmentQueues.delete(channelId);
  }

  private cancelOwnedPendingRelease(element: HTMLVideoElement): void {
    const pending = this.pendingReleases.get(element);
    if (!pending) return;
    this.pendingReleases.delete(element);
    sharedVideoElementOwnership.cancelPendingRelease(element, pending.token);
  }

  private scheduleRelease(channelId: string, element: HTMLVideoElement): void {
    if (sharedVideoElementOwnership.isClaimed(element)) return;
    if (this.pendingReleases.has(element)) return;

    const pending: PendingVideoRelease = {
      token: Symbol(`c6-video-release:${channelId}`),
      channelId,
      element,
    };
    this.pendingReleases.set(element, pending);
    sharedVideoElementOwnership.markPendingRelease(element, pending.token);
    this.enqueueMicrotask(() => this.finalizePendingRelease(pending));
  }

  private finalizePendingRelease(pending: PendingVideoRelease): void {
    if (this.pendingReleases.get(pending.element) !== pending) return;
    this.pendingReleases.delete(pending.element);
    if (sharedVideoElementOwnership.consumePendingRelease(pending.element, pending.token)) {
      this.releaseResource(pending.element);
    }
  }

  private flushPendingReleases(channelId?: string): void {
    for (const pending of [...this.pendingReleases.values()]) {
      if (channelId !== undefined && pending.channelId !== channelId) continue;
      this.finalizePendingRelease(pending);
    }
  }

  private releaseUnclaimedNow(element: HTMLVideoElement): void {
    this.cancelOwnedPendingRelease(element);
    sharedVideoElementOwnership.cancelPendingRelease(element);
    if (!sharedVideoElementOwnership.isClaimed(element)) {
      this.releaseResource(element);
    }
  }
}
